import { Dialog, IconButton } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import {
  TIMESTAMP_FORMAT_SPECS,
  TimeAnchor,
  TimestampFormat,
  formatAnchorTime,
  formatTimestamp,
  useTimestampFormat,
} from "@/shared/components/RobotoMarkdown/timeFormat";
import { useDateTimeFormat } from "@/shared/datetime";
import { CloseIcon } from "@/shared/foundation/icons/mui";
import { Button, TextInput } from "@/shared/foundation/primitives";
import {
  FractionDigits,
  isFractionDigits,
  nanoSecToSecStr,
} from "@/shared/time";

import { AnchorPickerPopover } from "./AnchorPickerPopover";
import styles from "./TimeFormatSettingsModal.module.css";

interface TimeFormatSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

/** Sample ns used for the in-modal preview — a realistic recent wall-clock
 *  instant so every format produces a legible example. */
const SAMPLE_NS = 1771481251468476035n;

/**
 * Stand-in t=0 for the Relative row's preview while the thread has none:
 * 65.123 s before the sample, so the row shows a relative reading rather than
 * an anchorless raw epoch.
 */
const SAMPLE_ANCHOR_NS = SAMPLE_NS - 65_123_000_000n;

const PRECISION_ERROR_MESSAGE = "Enter a whole number from 0 to 9";

/**
 * Digits the set t=0 is displayed to: all nine, so the epoch value on screen
 * is the exact instant relative times count from rather than a rounding of it.
 */
const ANCHOR_FRACTION_DIGITS: FractionDigits = 9;

/**
 * Lets the reader choose how timestamps in AI responses are rendered: the
 * format, how many decimal places it keeps below the second (every format but
 * the three integer ones honors that), and the t=0 that Relative counts from.
 *
 * There is nothing to configure without the preferences it configures, so
 * outside a TimestampFormatProvider it renders nothing. Its one mount site is
 * the thread input box, which every thread surface wraps in the provider.
 */
export function TimeFormatSettingsModal({
  open,
  onClose,
}: TimeFormatSettingsModalProps) {
  const ctx = useTimestampFormat();
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const dateTimeFormat = useDateTimeFormat();
  if (ctx === undefined) {
    return null;
  }

  const handlePickAnchor = (picked: TimeAnchor) => {
    ctx.setAnchor(picked);
  };

  const previewOptions = { ...ctx.renderOptions, dateTimeFormat };
  const previewFor = (format: TimestampFormat): string =>
    formatTimestamp(
      SAMPLE_NS,
      format,
      format === TimestampFormat.Relative && ctx.anchor === undefined
        ? { ...previewOptions, anchorNs: SAMPLE_ANCHOR_NS }
        : previewOptions,
    );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // The anchor picker is portaled outside the dialog; while it is open
      // the dialog's focus trap must not pull focus back out of its input.
      disableEnforceFocus={pickerOpen}
    >
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>Time format</h2>
          <p className={styles.subtitle}>
            Choose how timestamps in AI responses are displayed.
          </p>
        </div>
        <IconButton
          aria-label="Close time format settings"
          onClick={onClose}
          size="small"
          className={styles.closeButton}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div className={styles.body}>
        <div
          role="radiogroup"
          aria-label="Timestamp display format"
          className={styles.optionList}
        >
          {/* Not every format is always offerable: "Follow the visualizer"
              drops off the list when no visualizer is open. */}
          {ctx.availableFormats.map((format) => {
            const selected = ctx.format === format;
            return (
              <React.Fragment key={format}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={classNames(styles.option, {
                    [styles.optionSelected]: selected,
                  })}
                  onClick={() => ctx.setFormat(format)}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(styles.radio, {
                      [styles.radioSelected]: selected,
                    })}
                  />
                  <span className={styles.optionText}>
                    <span className={styles.label}>
                      {TIMESTAMP_FORMAT_SPECS[format].label}
                      <span className={styles.preview}>
                        {previewFor(format)}
                      </span>
                    </span>
                    <span className={styles.description}>
                      {TIMESTAMP_FORMAT_SPECS[format].description}
                    </span>
                  </span>
                </button>
                {/* Only Relative has a t=0 the reader chooses. Follow takes
                    the visualizer's, which is not this modal's to change. */}
                {selected && format === TimestampFormat.Relative && (
                  <AnchorInset
                    anchor={ctx.anchor}
                    pickerOpen={pickerOpen}
                    onPickerOpenChange={setPickerOpen}
                    onPick={handlePickAnchor}
                    onClear={() => ctx.setAnchor(undefined)}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <PrecisionRow
          precision={ctx.precision}
          onPrecisionChange={ctx.setPrecision}
        />
      </div>

      <div className={styles.footer}>
        <Button variant="contained" onClick={onClose} size="small">
          Done
        </Button>
      </div>
    </Dialog>
  );
}

interface PrecisionRowProps {
  precision: FractionDigits;
  onPrecisionChange: (precision: FractionDigits) => void;
}

/**
 * The decimal-places field: a free text entry rather than a row of buttons,
 * because every count from 0 to 9 is meaningful and a menu of five made the
 * other four look unavailable.
 *
 * Typed text is its own state so a keystroke that is not yet a valid count
 * ("1" on the way to "12", or a cleared field) can sit in the box without
 * yanking the previews to some other precision. A valid count commits
 * immediately — there is no Apply — and an invalid one leaves the committed
 * precision alone and says why in the row's hint slot.
 */
function PrecisionRow({ precision, onPrecisionChange }: PrecisionRowProps) {
  const [text, setText] = React.useState(() => String(precision));
  const parsed = parsePrecisionInput(text);

  const handleChange = (next: string) => {
    setText(next);
    const value = parsePrecisionInput(next);
    if (value !== undefined) {
      onPrecisionChange(value);
    }
  };

  return (
    <div className={styles.precisionRow}>
      <div className={styles.precisionText}>
        <span className={styles.label}>Decimal places</span>
        {parsed === undefined ? (
          <span className={styles.precisionError} role="alert">
            {PRECISION_ERROR_MESSAGE}
          </span>
        ) : (
          <span className={styles.description}>
            Applies to every format except the integer ones (ns, μs, ms).
          </span>
        )}
      </div>
      <TextInput
        className={styles.precisionInput}
        aria-label="Decimal places"
        aria-invalid={parsed === undefined}
        inputMode="numeric"
        value={text}
        onChange={(event) => handleChange(event.target.value)}
        autoComplete="off"
        spellCheck={false}
      />
    </div>
  );
}

/**
 * The typed text as a digit count, or undefined when it is not one. Blank and
 * whitespace are rejected before `Number`, which reads both as 0 — itself a
 * valid count, and not what an empty box means.
 */
function parsePrecisionInput(text: string): FractionDigits | undefined {
  const trimmed = text.trim();
  if (trimmed === "") {
    return undefined;
  }
  const parsed = Number(trimmed);
  return isFractionDigits(parsed) ? parsed : undefined;
}

interface AnchorInsetProps {
  anchor: TimeAnchor | undefined;
  pickerOpen: boolean;
  onPickerOpenChange: (open: boolean) => void;
  onPick: (anchor: TimeAnchor) => void;
  onClear: () => void;
}

/**
 * The Relative option's companion: the thread's t=0, where it came from, and
 * the controls that change it.
 *
 * Two states. With no t=0 chosen, Relative is not what timestamps actually
 * render as — they keep the reader's previous format — so the row says so and
 * offers the picker. With one chosen, it names the instant and where it came
 * from, and offers a repick or a clear.
 *
 * A set t=0 is read out twice, because the two forms answer different
 * questions. The local date and time comes first: it is what a reader checks
 * an anchor against ("yes, that run"). Under it sits the epoch value at full
 * nanosecond precision — the number every relative reading in the transcript
 * is subtracted from, and the one that gets handed to a script or a
 * colleague. That line ignores the decimal-places setting deliberately: this
 * is the anchor's identity, not a rendered timestamp, and a truncated epoch is
 * a different instant.
 */
function AnchorInset({
  anchor,
  pickerOpen,
  onPickerOpenChange,
  onPick,
  onClear,
}: AnchorInsetProps) {
  const dateTimeFormat = useDateTimeFormat();
  if (anchor === undefined) {
    return (
      <div className={styles.anchorInset}>
        <div className={styles.anchorText}>
          <span className={styles.anchorTitle}>t=0 — not set</span>
          <span className={styles.anchorHint}>
            Pick a start time to count from. Until then, timestamps keep their
            previous format.
          </span>
        </div>
        <AnchorPickerPopover
          open={pickerOpen}
          onOpenChange={onPickerOpenChange}
          triggerLabel="Pick…"
          onPick={onPick}
        />
      </div>
    );
  }

  const localTime = formatAnchorTime(anchor.ns, dateTimeFormat);
  // Two of the three ways to set an anchor label it with the instant itself —
  // the picker's text field, and "Set as t=0" on a rendered timestamp — and
  // that label is already the line below. Only a label that says where the
  // instant came from ("Start of ds_abc") is worth the title's other half.
  const provenance = isOwnLocalTime(anchor.label, localTime)
    ? undefined
    : anchor.label;

  return (
    <div className={styles.anchorInset}>
      <div className={styles.anchorText}>
        <span className={styles.anchorTitle}>
          {provenance === undefined ? "t=0" : `t=0 — ${provenance}`}
        </span>
        <span className={styles.anchorLocalTime}>{localTime}</span>
        <span className={styles.anchorTime}>
          {nanoSecToSecStr(anchor.ns, ANCHOR_FRACTION_DIGITS)} s
        </span>
      </div>
      <div className={styles.anchorActions}>
        <AnchorPickerPopover
          open={pickerOpen}
          onOpenChange={onPickerOpenChange}
          triggerLabel="Change…"
          onPick={onPick}
        />
        <Button variant="text" size="small" onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  );
}

/**
 * Whether `label` is a rendering of the anchor's own instant rather than
 * provenance. The fractional second is dropped from both sides before
 * comparing, so an anchor stored by a build that rendered a different number
 * of decimals is still recognized as the same reading and not printed twice.
 */
function isOwnLocalTime(label: string, localTime: string): boolean {
  const withoutFraction = (text: string) => text.replace(/\.\d+/, "");
  return withoutFraction(label) === withoutFraction(localTime);
}
